import { describeApiError, type SalaryModel } from '@brush/api-client';
import {
  ErrorNotice,
  PlatformBadge,
  blankSalaryModel,
  referenceSalaryModel,
  toSalaryDocument,
} from '@brush/ui';
import { useState } from 'react';
import { useCloneSalaryModel, useCreateSalaryModel } from '../../queries';
import { useRoom } from '../../room';
import { Modal, btnGhost, btnPrimary, modalInput } from '../Modal';

/**
 * What the dialog was opened to do.
 *
 * `clone` is the server's own clone route, not a create with a copied body — the same route, and
 * the same semantics, as cloning a points system (§4.1). The copy lands as version 1, `draft`,
 * whatever it was copied from, and records where it came from. Cloning checks two entitlements
 * server-side: seeing the source, and writing to this room.
 */
export type NewSalaryModelIntent =
  | { mode: 'create'; start: 'blank' | 'reference' }
  | { mode: 'clone'; source: SalaryModel; versionNumber: number | null };

export function NewSalaryModelDialog({
  intent,
  onClose,
  onCreated,
}: {
  intent: NewSalaryModelIntent;
  onClose: () => void;
  /** Hands back the new model so the pane can open the builder on it straight away. */
  onCreated: (model: SalaryModel) => void;
}) {
  const { roomId, room } = useRoom();
  const cloning = intent.mode === 'clone';

  const [name, setName] = useState(() =>
    cloning
      ? // A room taking a platform model keeps its name: it is that model, as this room runs it.
        // Copying its own gets a "(copy)" so the two are tellable apart in the list.
        intent.source.isPlatform
        ? intent.source.name
        : `${intent.source.name} (copy)`
      : intent.start === 'reference'
        ? 'Frequency-weighted pricing'
        : '',
  );

  const create = useCreateSalaryModel(roomId);
  const clone = useCloneSalaryModel(roomId);
  const pending = create.isPending || clone.isPending;

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;

    if (cloning) {
      clone.mutate(
        { id: intent.source.id, name: trimmed, version: intent.versionNumber },
        { onSuccess: onCreated },
      );
      return;
    }

    const definition =
      intent.start === 'reference' ? referenceSalaryModel(trimmed) : blankSalaryModel(trimmed);

    create.mutate(
      {
        name: trimmed,
        description: definition.description,
        definition: toSalaryDocument(definition),
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <Modal
      title={cloning ? 'Make this model your own' : 'New salary model'}
      subtitle={
        cloning
          ? `A copy owned by ${room?.name ?? 'this room'}, free to change.`
          : `Owned by ${room?.name ?? 'this room'}. Nobody else can see or use it.`
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={name.trim().length === 0 || pending}
            className={btnPrimary}
          >
            {pending ? 'Creating…' : cloning ? 'Create copy' : 'Create and open'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs text-ink-800/60">Name</span>
          <input
            type="text"
            value={name}
            autoFocus
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Series pricing"
            className={modalInput}
          />
        </label>

        {cloning ? (
          <div className="space-y-2 text-xs leading-relaxed text-ink-800/60">
            <p className="flex flex-wrap items-center gap-2">
              Copying
              {intent.source.isPlatform ? <PlatformBadge /> : null}
              <span className="font-medium text-ink-950">{intent.source.name}</span>
              {intent.versionNumber !== null ? (
                <span className="num text-ink-800/45">version {intent.versionNumber}</span>
              ) : null}
            </p>
            <p>
              The copy starts as your own draft and is independent from the moment it exists —
              changes to it never touch the original, and changes to the original never reach it.
              Nothing is published until you publish it.
            </p>
            {intent.versionNumber === null ? (
              <p className="text-warn">
                That model has no versions yet, so there is nothing to copy from — the copy will
                start empty.
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-xs leading-relaxed text-ink-800/60">
            {intent.start === 'reference'
              ? 'Starts from the reference model: priced mostly on how often somebody cashes, with leaderboard points and money behind it, five price bands and a floor. Every figure is yours to change.'
              : 'Starts from the plainest model that still works — one signal, three prices, a floor. You set the rest on the next screen.'}
          </p>
        )}

        <ErrorNotice message={describeApiError(create.error) ?? describeApiError(clone.error)} />
      </div>
    </Modal>
  );
}
